import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  InlineStack,
  Layout,
  Modal,
  Page,
  RadioButton,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ApiError } from "../api/client";
import {
  getInventoryReconciliation,
  inventoryReconciliationKeys,
  listInventoryReconciliations,
  reconcileInventoryOperation,
  type InventoryReconcileInput,
  type InventoryReconciliationDecision,
  type InventoryReconciliationKind,
  type InventoryReconciliationOperation,
  type JsonValue,
} from "../api/inventory_reconciliation";
import { canonicalStockyPublicBigIntId } from "../api/stocky_public_ids";
import { t } from "../lib/i18n";
import { formatDateTime } from "../utils/date";

const KIND_LABELS: Record<InventoryReconciliationKind, string> = {
  inventory_adjustment: "在庫調整",
  purchase_order_receive: "注文書の入荷",
  inventory_transfer_ship: "拠点間移動（出庫）",
  inventory_transfer_receive: "拠点間移動（入庫）",
  stocktake_apply: "棚卸しの確定",
  stocktake_apply_chunk: "棚卸しの確定",
};

type ConfirmMode = "decision" | "retry_sync";

function kindLabel(kind: InventoryReconciliationKind): string {
  return t(KIND_LABELS[kind] ?? kind);
}

function statusBadge(operation: InventoryReconciliationOperation) {
  if (operation.status === "unknown") {
    return <Badge tone="critical">{t("要確認")}</Badge>;
  }
  if (operation.status === "sync_pending") {
    return <Badge tone="warning">{t("実在庫の同期待ち")}</Badge>;
  }
  if (operation.status === "completed") {
    return <Badge tone="success">{t("照合済み・同期完了")}</Badge>;
  }
  if (operation.status === "failed") {
    return <Badge tone="info">{t("未適用で確定")}</Badge>;
  }
  return <Badge>{operation.status}</Badge>;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function tuples(
  payload: Record<string, unknown> | null,
  key: string,
): unknown[][] {
  const value = payload?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter(Array.isArray) as unknown[][];
}

function visibleId(value: unknown): string {
  return typeof value === "number" || typeof value === "string"
    ? String(value)
    : "—";
}

function signed(value: unknown): string {
  return typeof value === "number" && value > 0
    ? `+${value}`
    : visibleId(value);
}

function operationTarget(operation: InventoryReconciliationOperation): string {
  const id = operation.resource_id;
  switch (operation.kind) {
    case "purchase_order_receive":
      return id === null
        ? t("注文書（保存IDなし）")
        : t("注文書 #{id}", { id });
    case "inventory_transfer_ship":
    case "inventory_transfer_receive":
      return id === null
        ? t("拠点間移動（保存IDなし）")
        : t("拠点間移動 #{id}", { id });
    case "stocktake_apply":
    case "stocktake_apply_chunk":
      return id === null
        ? t("棚卸し（保存IDなし）")
        : t("棚卸し #{id}", { id });
    default:
      return t("在庫調整 #{id}", { id: operation.id });
  }
}

function operationOrigin(kind: InventoryReconciliationKind): {
  label: string;
  path: string;
} {
  switch (kind) {
    case "purchase_order_receive":
      return { label: t("入荷を開く"), path: "/placed-orders" };
    case "inventory_transfer_ship":
    case "inventory_transfer_receive":
      return { label: t("拠点間移動を開く"), path: "/inventory-transfers" };
    case "stocktake_apply":
    case "stocktake_apply_chunk":
      return { label: t("棚卸しを開く"), path: "/stocktakes" };
    default:
      return { label: t("在庫調整を開く"), path: "/inventory-adjustments" };
  }
}

function requestLines(operation: InventoryReconciliationOperation): string[] {
  const payload = record(operation.request_payload);
  if (!payload) return [];
  const lines: string[] = [];

  const locationId = payload.location_id;
  const sourceLocationId = payload.source_location_id;
  const destLocationId = payload.dest_location_id;
  if (locationId !== undefined) {
    lines.push(t("対象拠点（保存ID）: {id}", { id: visibleId(locationId) }));
  }
  if (sourceLocationId !== undefined) {
    lines.push(
      t("出庫元拠点（保存ID）: {id}", {
        id: visibleId(sourceLocationId),
      }),
    );
  }
  if (destLocationId !== undefined) {
    lines.push(
      t("入庫先拠点（保存ID）: {id}", { id: visibleId(destLocationId) }),
    );
  }
  if (typeof payload.reason === "string") {
    lines.push(t("理由: {reason}", { reason: payload.reason }));
  }
  if (typeof payload.note === "string" && payload.note.trim() !== "") {
    lines.push(t("操作メモ: {note}", { note: payload.note }));
  }

  if (operation.kind === "inventory_adjustment") {
    for (const [variantId, delta] of tuples(payload, "entries")) {
      lines.push(
        t("Shopifyバリアント {variantId}: 在庫を {delta} 調整", {
          variantId: visibleId(variantId),
          delta: signed(delta),
        }),
      );
    }
  } else if (operation.kind === "purchase_order_receive") {
    for (const [itemId, quantity] of tuples(payload, "receipts")) {
      lines.push(
        t("発注明細 {itemId}: {quantity} 個を入荷", {
          itemId: visibleId(itemId),
          quantity: visibleId(quantity),
        }),
      );
    }
  } else if (operation.kind === "inventory_transfer_ship") {
    for (const [variantId, quantity] of tuples(payload, "items")) {
      lines.push(
        t("Shopifyバリアント {variantId}: {quantity} 個を出庫", {
          variantId: visibleId(variantId),
          quantity: visibleId(quantity),
        }),
      );
    }
  } else if (operation.kind === "inventory_transfer_receive") {
    for (const [variantId, quantity] of tuples(payload, "receipts")) {
      lines.push(
        t("Shopifyバリアント {variantId}: {quantity} 個を入庫", {
          variantId: visibleId(variantId),
          quantity: visibleId(quantity),
        }),
      );
    }
  } else if (
    operation.kind === "stocktake_apply" ||
    operation.kind === "stocktake_apply_chunk"
  ) {
    for (const [variantId, quantity] of tuples(payload, "counts")) {
      lines.push(
        t("Shopifyバリアント {variantId}: 実数 {quantity} 個", {
          variantId: visibleId(variantId),
          quantity: visibleId(quantity),
        }),
      );
    }
  }
  return lines;
}

function externalLines(operation: InventoryReconciliationOperation): string[] {
  const payload = record(operation.external_payload);
  const intents = Array.isArray(payload?.intents) ? payload.intents : [];
  return intents.flatMap((raw, index) => {
    const intent = record(raw);
    if (!intent) return [];
    const common = t("在庫アイテム {itemId} / Shopify拠点 {locationId}", {
      itemId: visibleId(intent.inventory_item_id),
      locationId: visibleId(intent.location_id),
    });
    if (intent.delta !== undefined) {
      return [
        t("{target}: 増減 {delta}", {
          target: common,
          delta: signed(intent.delta),
        }),
      ];
    }
    if (intent.quantity !== undefined) {
      const before =
        intent.change_from_quantity === undefined
          ? ""
          : t("（送信時の変更元 {quantity}）", {
              quantity: visibleId(intent.change_from_quantity),
            });
      return [
        t("{target}: 実数 {quantity}{before}", {
          target: common,
          quantity: visibleId(intent.quantity),
          before,
        }),
      ];
    }
    return [
      t("保存済み在庫項目 {index}: {target}", {
        index: index + 1,
        target: common,
      }),
    ];
  });
}

function evidenceLines(operation: InventoryReconciliationOperation): string[] {
  const evidence = record(operation.reconciliation_evidence);
  if (!evidence) return [];
  const lines: string[] = [];
  if (evidence.observed_at !== undefined) {
    lines.push(
      t("Shopifyで確認した日時: {value}", {
        value: visibleId(evidence.observed_at),
      }),
    );
  }
  if (evidence.source !== undefined) {
    lines.push(
      t("確認した画面: {value}", { value: visibleId(evidence.source) }),
    );
  }
  if (evidence.reference !== undefined) {
    lines.push(
      t("確認した根拠: {value}", {
        value: visibleId(evidence.reference),
      }),
    );
  }
  return lines;
}

function SavedLines({
  title,
  lines,
  empty,
}: {
  title: string;
  lines: string[];
  empty: string;
}) {
  return (
    <Box background="bg-surface-secondary" borderRadius="300" padding="400">
      <BlockStack gap="200">
        <Text as="h3" variant="headingSm">
          {title}
        </Text>
        {(lines.length > 0 ? lines : [empty]).map((line, index) => (
          <Text as="p" key={`${index}-${line}`} tone="subdued">
            {line}
          </Text>
        ))}
      </BlockStack>
    </Box>
  );
}

function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return t(
        "セッションを確認できませんでした（401）。Shopify 管理画面からアプリを再読み込みしてください。",
      );
    }
    if (error.status === 404) {
      return t(
        "このストアで確認できる在庫操作が見つかりませんでした（404）。一覧を再読み込みしてください。",
      );
    }
    if (error.status === 409) {
      if (isReconciliationDrainConflict(error)) {
        return t(
          "送信中だった処理と照合判断が重ならないよう、安全待機中です（409）: {detail}。初回送信から24時間経過後に状態を再取得し、Shopify の実在庫をもう一度確認してから判断してください。",
          { detail: error.detail },
        );
      }
      return t(
        "在庫操作の状態が変わったか、同じストアに別の未解決操作が残っているため確定できませんでした（409）: {detail}。未解決の一覧を確認し、他の操作を先に判断してください。",
        { detail: error.detail },
      );
    }
    if (error.status === 422) {
      return t("入力内容を確認してください（422）: {detail}", {
        detail: error.detail,
      });
    }
    if (error.status === 502) {
      return t(
        "照合結果は保存されましたが、Shopify の実在庫同期に失敗しました（502）。この操作は「実在庫の同期待ち」のまま保護されています。同じ画面から同期を再開してください。",
      );
    }
    if (error.status >= 500) {
      return t(
        "サーバー処理に失敗しました（{status}）: {detail}。状態を再取得してから同じ判断を再試行できます。",
        {
          status: error.status,
          detail: error.detail,
        },
      );
    }
    return t("在庫操作の照合に失敗しました（{status}）: {detail}", {
      status: error.status,
      detail: error.detail,
    });
  }
  return error instanceof Error ? error.message : String(error);
}

function isReconciliationDrainConflict(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    error.detail.includes("24時間")
  );
}

function asJsonEvidence(
  value: Record<string, unknown> | null,
): Record<string, JsonValue> {
  // API response is JSON.  The recursive type assertion is confined here so
  // callers cannot accidentally put Date/File/etc. into the retry body.
  return (value ?? {
    source: "existing_reconciliation_audit",
  }) as Record<string, JsonValue>;
}

function InventoryReconciliationList() {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: inventoryReconciliationKeys.unresolved,
    queryFn: () => listInventoryReconciliations(false),
  });

  return (
    <Page
      title={t("在庫反映の確認")}
      subtitle={t(
        "Shopify への書き込み結果を確認できなかった操作を、安全に照合します",
      )}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <Banner tone="warning" title={t("二重反映を防ぐための確認です")}>
              {t(
                "ここにある操作は、通信切断などにより Shopify へ反映されたかをアプリが断定できません。確認が終わるまで同じ在庫操作は再実行しないでください。",
              )}
            </Banner>
            {query.isLoading ? (
              <Card>
                <SkeletonBodyText lines={4} />
              </Card>
            ) : query.isError ? (
              <Banner
                tone="critical"
                title={t("在庫操作の一覧を取得できませんでした")}
                action={{
                  content: t("再試行"),
                  onAction: () => void query.refetch(),
                }}
              >
                {errorText(query.error)}
              </Banner>
            ) : (query.data?.operations.length ?? 0) === 0 ? (
              <Banner
                tone="success"
                title={t("確認が必要な在庫操作はありません")}
              >
                {t(
                  "Shopify への反映結果が不明な操作や、実在庫の同期待ちの操作はありません。",
                )}
              </Banner>
            ) : (
              query.data?.operations.map((operation) => (
                <Card key={operation.id}>
                  <BlockStack gap="300">
                    <InlineStack
                      align="space-between"
                      blockAlign="center"
                      gap="200"
                    >
                      <BlockStack gap="100">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="h2" variant="headingMd">
                            {kindLabel(operation.kind)}
                          </Text>
                          {statusBadge(operation)}
                        </InlineStack>
                        <Text as="p" tone="subdued">
                          {operationTarget(operation)} ·{" "}
                          {formatDateTime(operation.created_at)}
                        </Text>
                      </BlockStack>
                      <Button
                        variant="primary"
                        onClick={() =>
                          navigate(`/inventory-reconciliation/${operation.id}`)
                        }
                      >
                        {operation.status === "sync_pending"
                          ? t("同期を再開")
                          : t("内容を確認")}
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </Card>
              ))
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function InventoryReconciliationDetail({
  operationId,
}: {
  operationId: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [decision, setDecision] =
    useState<InventoryReconciliationDecision | null>(null);
  const [note, setNote] = useState("");
  const [observedAt, setObservedAt] = useState("");
  const [evidenceReference, setEvidenceReference] = useState("");
  const [confirmMode, setConfirmMode] = useState<ConfirmMode | null>(null);
  // React の再描画で loading/disabled が反映される前の高速な二重clickも遮断する。
  // backend も同じ operation をlockするが、確認POST自体を画面から二重送信しない。
  const submissionLock = useRef(false);

  const query = useQuery({
    queryKey: inventoryReconciliationKeys.detail(operationId),
    queryFn: () => getInventoryReconciliation(operationId),
  });
  const operation = query.data;

  useEffect(() => {
    if (!operation) return;
    setDecision(operation.reconciliation_decision);
    setNote(operation.reconciliation_note ?? "");
    const evidence = record(operation.reconciliation_evidence);
    setObservedAt(
      typeof evidence?.observed_at === "string" ? evidence.observed_at : "",
    );
    setEvidenceReference(
      typeof evidence?.reference === "string" ? evidence.reference : "",
    );
    setConfirmMode(null);
  }, [operation?.id]);

  const mutation = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: InventoryReconcileInput;
    }) => reconcileInventoryOperation(id, input),
    onSuccess: (result) => {
      setConfirmMode(null);
      queryClient.setQueryData(
        inventoryReconciliationKeys.detail(result.id),
        result,
      );
      void queryClient.invalidateQueries({
        queryKey: inventoryReconciliationKeys.unresolved,
      });
    },
    onError: () => {
      setConfirmMode(null);
      // The reconciliation decision is committed before the authoritative
      // read.  A 502/ambiguous 5xx therefore requires a fresh GET to reveal
      // sync_pending instead of leaving the stale UNKNOWN form on screen.
      void queryClient.invalidateQueries({
        queryKey: inventoryReconciliationKeys.detail(operationId),
      });
      void queryClient.invalidateQueries({
        queryKey: inventoryReconciliationKeys.unresolved,
      });
    },
    onSettled: () => {
      submissionLock.current = false;
    },
  });

  const canOpenConfirmation =
    decision !== null &&
    note.trim() !== "" &&
    observedAt.trim() !== "" &&
    evidenceReference.trim() !== "" &&
    !mutation.isPending;

  const submitDecision = () => {
    if (
      !operation ||
      !decision ||
      !canOpenConfirmation ||
      submissionLock.current
    )
      return;
    submissionLock.current = true;
    mutation.mutate({
      id: operation.id,
      input: {
        decision,
        confirmed: true,
        note: note.trim(),
        evidence: {
          source: "shopify_admin_inventory_history",
          observed_at: observedAt.trim(),
          reference: evidenceReference.trim(),
        },
      },
    });
  };

  const retrySync = () => {
    if (
      !operation ||
      operation.status !== "sync_pending" ||
      submissionLock.current
    )
      return;
    submissionLock.current = true;
    mutation.mutate({
      id: operation.id,
      input: {
        decision: "applied",
        confirmed: true,
        note:
          operation.reconciliation_note?.trim() ||
          "Resume authoritative Shopify inventory sync",
        evidence: asJsonEvidence(operation.reconciliation_evidence),
      },
    });
  };

  const finalDecision = decision;
  const confirmationTitle =
    confirmMode === "retry_sync"
      ? t("Shopify 実在庫の同期を再試行しますか？")
      : finalDecision === "applied"
        ? t("「Shopify に反映済み」で確定しますか？")
        : t("「Shopify に反映されていない」で確定しますか？");

  return (
    <Page
      title={t("在庫反映の確認")}
      backAction={{
        content: t("未解決の一覧へ"),
        onAction: () => navigate("/inventory-reconciliation"),
      }}
    >
      <Modal
        open={confirmMode !== null}
        onClose={() => {
          if (!mutation.isPending) setConfirmMode(null);
        }}
        title={confirmationTitle}
        primaryAction={{
          content:
            confirmMode === "retry_sync"
              ? t("実在庫の同期を再試行する")
              : finalDecision === "applied"
                ? t("適用済みと確定する")
                : t("未適用と確定する"),
          destructive:
            confirmMode !== "retry_sync" && finalDecision === "not_applied",
          loading: mutation.isPending,
          disabled: mutation.isPending,
          onAction: confirmMode === "retry_sync" ? retrySync : submitDecision,
        }}
        secondaryActions={[
          {
            content: t("戻って確認する"),
            disabled: mutation.isPending,
            onAction: () => setConfirmMode(null),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {confirmMode === "retry_sync" ? (
              <Text as="p">
                {t(
                  "業務記録はすでに一度だけ確定済みです。再試行では業務記録を増やさず、Shopify から対象商品の実在庫を読み直してアプリの在庫表示を置き換えます。",
                )}
              </Text>
            ) : finalDecision === "applied" ? (
              <Text as="p">
                {t(
                  "Shopify にこの操作がすでに反映されている前提で、保存済みの業務記録を一度だけ確定します。その後、Shopify の実在庫を読み直します。",
                )}
              </Text>
            ) : (
              <Text as="p" tone="critical">
                {t(
                  "Shopify にこの操作が反映されていないと確定し、新しい在庫操作を許可します。実際には反映済みだった場合、あとで再実行すると二重に在庫が動きます。",
                )}
              </Text>
            )}
            <Text as="p" fontWeight="semibold">
              {t(
                "このボタンを押す前に、Shopify 管理画面の実在庫と在庫調整履歴をもう一度確認してください。",
              )}
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {query.isLoading ? (
              <Card>
                <SkeletonBodyText lines={8} />
              </Card>
            ) : query.isError ? (
              <Banner
                tone="critical"
                title={t("在庫操作の詳細を取得できませんでした")}
                action={{
                  content: t("再試行"),
                  onAction: () => void query.refetch(),
                }}
              >
                {errorText(query.error)}
              </Banner>
            ) : operation ? (
              <>
                <Banner
                  tone="critical"
                  title={t("Shopify 管理画面の実在庫を直接確認してください")}
                >
                  {t(
                    "この画面に保存された内容だけで判断しないでください。Shopify 管理画面の「商品 ＞ 在庫」と在庫調整履歴を開き、対象拠点・対象商品・数量・時刻が一致するか確認してください。",
                  )}
                </Banner>

                {mutation.isError && (
                  <Banner
                    tone="critical"
                    title={t("照合処理を完了できませんでした")}
                    action={{
                      content:
                        mutation.error instanceof ApiError &&
                        mutation.error.status === 409 &&
                        !isReconciliationDrainConflict(mutation.error)
                          ? t("未解決の一覧へ")
                          : t("状態を再取得"),
                      onAction:
                        mutation.error instanceof ApiError &&
                        mutation.error.status === 409 &&
                        !isReconciliationDrainConflict(mutation.error)
                          ? () => navigate("/inventory-reconciliation")
                          : () => void query.refetch(),
                    }}
                  >
                    {errorText(mutation.error)}
                  </Banner>
                )}

                {operation.status === "sync_pending" && (
                  <Banner
                    tone="warning"
                    title={t(
                      "照合済みですが、Shopify 実在庫の同期が未完了です",
                    )}
                    action={{
                      content: t("Shopify 実在庫の同期を再試行"),
                      onAction: () => setConfirmMode("retry_sync"),
                    }}
                  >
                    {t(
                      "業務記録は一度だけ確定済みです。新しい在庫操作は保護のため停止中です。同期を再試行すると、業務記録を増やさず Shopify の現在庫だけを読み直します。",
                    )}
                  </Banner>
                )}

                <Card>
                  <BlockStack gap="400">
                    <InlineStack
                      align="space-between"
                      blockAlign="center"
                      gap="200"
                    >
                      <BlockStack gap="100">
                        <Text as="h2" variant="headingLg">
                          {kindLabel(operation.kind)}
                        </Text>
                        <Text as="p" tone="subdued">
                          {operationTarget(operation)} ·{" "}
                          {formatDateTime(operation.created_at)}
                        </Text>
                      </BlockStack>
                      {statusBadge(operation)}
                    </InlineStack>
                    <Divider />
                    <SavedLines
                      title={t("操作時に保存した内容")}
                      lines={requestLines(operation)}
                      empty={t(
                        "保存内容を項目別に表示できません。判断せずサポートへ連絡してください。",
                      )}
                    />
                    <SavedLines
                      title={t("Shopify へ送信するため保存した内容")}
                      lines={externalLines(operation)}
                      empty={t(
                        "Shopify 送信用の保存内容を表示できません。判断せずサポートへ連絡してください。",
                      )}
                    />

                    {operation.reconciliation_decision !== null && (
                      <SavedLines
                        title={t("保存済みの照合記録")}
                        lines={[
                          operation.reconciliation_decision === "applied"
                            ? t("判断: Shopify に反映済み")
                            : t("判断: Shopify に反映されていない"),
                          t("確定日時: {value}", {
                            value: formatDateTime(operation.reconciled_at),
                          }),
                          ...(operation.reconciliation_note
                            ? [
                                t("確認メモ: {value}", {
                                  value: operation.reconciliation_note,
                                }),
                              ]
                            : []),
                          ...evidenceLines(operation),
                        ]}
                        empty={t("照合記録はありません。")}
                      />
                    )}
                  </BlockStack>
                </Card>

                {operation.status === "unknown" &&
                  operation.reconciliation_decision === null && (
                    <Card>
                      <BlockStack gap="400">
                        <Text as="h2" variant="headingMd">
                          {t("Shopify で確認した結果を入力")}
                        </Text>
                        <Text as="p" tone="subdued">
                          {t(
                            "どちらかを選んだだけでは確定しません。必須項目を入力して「確認画面へ」を押したあと、内容をもう一度確認します。",
                          )}
                        </Text>
                        <BlockStack gap="200">
                          <RadioButton
                            label={t("Shopify に反映済み")}
                            helpText={t(
                              "保存済みの業務記録を確定し、Shopify の実在庫を読み直します。",
                            )}
                            checked={decision === "applied"}
                            id="reconciliation-applied"
                            name="reconciliation-decision"
                            onChange={(checked) =>
                              checked && setDecision("applied")
                            }
                          />
                          <RadioButton
                            label={t("Shopify に反映されていない")}
                            helpText={t(
                              "この操作を未適用として閉じ、新しい在庫操作を許可します。",
                            )}
                            checked={decision === "not_applied"}
                            id="reconciliation-not-applied"
                            name="reconciliation-decision"
                            onChange={(checked) =>
                              checked && setDecision("not_applied")
                            }
                          />
                        </BlockStack>
                        <TextField
                          label={t("確認メモ（必須）")}
                          value={note}
                          onChange={setNote}
                          multiline={3}
                          maxLength={2000}
                          showCharacterCount
                          autoComplete="off"
                          placeholder={t(
                            "例: Shopify の在庫履歴と現在庫を照合しました",
                          )}
                        />
                        <TextField
                          label={t("Shopify で確認した日時（必須）")}
                          value={observedAt}
                          onChange={setObservedAt}
                          maxLength={255}
                          autoComplete="off"
                          placeholder="2026-08-09 14:30 JST"
                          helpText={t(
                            "Shopify 管理画面を確認した日時を、タイムゾーンを含めて入力してください。",
                          )}
                        />
                        <TextField
                          label={t("確認した画面・根拠（必須）")}
                          value={evidenceReference}
                          onChange={setEvidenceReference}
                          multiline={2}
                          maxLength={8000}
                          showCharacterCount
                          autoComplete="off"
                          placeholder={t(
                            "例: 商品 ＞ 在庫 ＞ 調整履歴で -3 の記録を確認",
                          )}
                        />
                        <InlineStack align="end">
                          <Button
                            variant="primary"
                            disabled={!canOpenConfirmation}
                            loading={mutation.isPending}
                            onClick={() => setConfirmMode("decision")}
                          >
                            {t("確認画面へ")}
                          </Button>
                        </InlineStack>
                      </BlockStack>
                    </Card>
                  )}

                {(operation.status === "completed" ||
                  operation.status === "failed") && (
                  <Banner
                    tone="success"
                    title={t("この在庫操作の照合は完了しています")}
                    action={{
                      content: operationOrigin(operation.kind).label,
                      onAction: () =>
                        navigate(operationOrigin(operation.kind).path),
                    }}
                  >
                    {operation.status === "completed"
                      ? t(
                          "Shopify の実在庫同期まで完了しました。この操作を再確定する必要はありません。",
                        )
                      : t(
                          "Shopify には反映されていない判断で完了しました。必要なら元の在庫画面から新しい操作として実行してください。",
                        )}
                  </Banner>
                )}
              </>
            ) : null}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export function InventoryReconciliation() {
  const params = useParams<{ operationId?: string }>();
  if (params.operationId === undefined) {
    return <InventoryReconciliationList />;
  }
  let operationId: string;
  try {
    operationId = canonicalStockyPublicBigIntId(params.operationId);
  } catch {
    return (
      <Page title={t("在庫反映の確認")}>
        <Banner tone="critical">
          {t(
            "在庫操作IDが正しくありません。未解決の一覧から開き直してください。",
          )}
        </Banner>
      </Page>
    );
  }
  return <InventoryReconciliationDetail operationId={operationId} />;
}
